import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AdminGate } from "@/components/AdminShell";
import {
  adminApi,
  type AdminInquirySummary,
  type AdminInquiryTimeline,
  type ApiError,
} from "@/lib/api";
import { describeEvent, severityClasses, type TimelineEvent } from "@/lib/timelineEvents";

// ---------------------------------------------------------------------------
// Admin /inquiries — the R1 per-inquiry timeline ("everything that happened
// for inquiry X"). Retires the hand-run forensics SQL: picker list → vertical
// timeline with failures in red, header card, vouch chain.
//
// Served on admin.digavouch.com beside /tenants; same Google sign-in gate,
// access enforced server-side (authenticateAdmin allowlist). This is a
// founders' debugging surface: emails and provider reasons render unmasked
// and raw detail JSON is always one <details> away — it must never hide data.
// ---------------------------------------------------------------------------

export default function InquiryTimeline() {
  return <AdminGate wide>{(idToken, signOut) => <InquiriesAdmin idToken={idToken} onSignOut={signOut} />}</AdminGate>;
}

export function InquiriesAdmin({ idToken, onSignOut }: { idToken: string; onSignOut: () => void }) {
  const { inquiryId } = useParams<{ inquiryId: string }>();
  const navigate = useNavigate();

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-slate-800">
          {inquiryId ? "Inquiry timeline" : "Inquiries"}
        </h2>
        <div className="flex items-center gap-2">
          {inquiryId && (
            <Button variant="outline" size="sm" onClick={() => navigate("/inquiries")}>
              ← All inquiries
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={onSignOut}>
            Sign out
          </Button>
        </div>
      </div>
      {inquiryId ? (
        <TimelineDetail idToken={idToken} inquiryId={inquiryId} />
      ) : (
        <InquiryPicker idToken={idToken} onOpen={(id) => navigate(`/inquiries/${id}`)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function InquiryPicker({ idToken, onOpen }: { idToken: string; onOpen: (id: string) => void }) {
  const [q, setQ] = useState("");
  const [days, setDays] = useState(7);
  const [rows, setRows] = useState<AdminInquirySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { inquiries } = await adminApi.listInquiries(idToken, { q, days });
      setRows(inquiries);
    } catch (err) {
      const e = err as ApiError;
      setError(e.status === 401 || e.status === 403 ? "Not authorized (sign in with an admin account)." : e.message);
    } finally {
      setLoading(false);
    }
  }, [idToken, q, days]);

  useEffect(() => {
    void load();
    // Re-query on window change immediately; q is applied on submit below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken, days]);

  return (
    <div>
      <form
        className="mb-4 flex items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void load();
        }}
      >
        <input
          className="w-72 rounded-md border border-slate-300 px-3 py-2 text-sm"
          placeholder="Search name or email…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select
          className="rounded-md border border-slate-300 px-2 py-2 text-sm"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          aria-label="Window"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
        <Button type="submit" variant="outline" size="sm">
          Search
        </Button>
      </form>

      {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3 mb-4">{error}</p>}
      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-500">No inquiries in this window.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">Created</th>
                <th className="px-4 py-2">Org</th>
                <th className="px-4 py-2">Applicant</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Zone</th>
                <th className="px-4 py-2">Vouches</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.inquiry_id} className="border-t border-slate-100">
                  <td className="px-4 py-2 text-slate-600 whitespace-nowrap">{fmtTime(r.created_at)}</td>
                  <td className="px-4 py-2 text-slate-600">{r.org_name}</td>
                  <td className="px-4 py-2">
                    <span className="font-medium text-slate-800">{r.applicant_name ?? "—"}</span>
                    <span className="ml-2 text-xs text-slate-500">{r.applicant_email ?? ""}</span>
                  </td>
                  <td className="px-4 py-2 text-slate-600">{r.status}</td>
                  <td className="px-4 py-2 text-slate-600">{r.current_zone ?? "—"}</td>
                  <td className="px-4 py-2 text-slate-600">
                    {r.vouches_completed}/{r.voucher_invites}
                  </td>
                  <td className="px-4 py-2">
                    <Button variant="outline" size="sm" onClick={() => onOpen(r.inquiry_id)}>
                      Timeline
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function TimelineDetail({ idToken, inquiryId }: { idToken: string; inquiryId: string }) {
  const [data, setData] = useState<AdminInquiryTimeline | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    adminApi
      .inquiryTimeline(idToken, inquiryId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        const e = err as ApiError;
        if (!cancelled)
          setError(
            e.status === 404
              ? "No inquiry with that id."
              : e.status === 401 || e.status === 403
                ? "Not authorized (sign in with an admin account)."
                : e.message,
          );
      });
    return () => {
      cancelled = true;
    };
  }, [idToken, inquiryId]);

  if (error) return <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">{error}</p>;
  if (!data) return <p className="text-sm text-slate-500">Loading…</p>;

  const { inquiry, invitations, events } = data;
  return (
    <div className="space-y-6">
      <HeaderCard inquiry={inquiry} />
      {invitations.length > 0 && <VouchChain invitations={invitations} />}
      <EventList events={events} />
    </div>
  );
}

function HeaderCard({ inquiry }: { inquiry: AdminInquiryTimeline["inquiry"] }) {
  const facts: Array<[string, string | null]> = [
    ["Org", inquiry.org_name],
    ["Applicant", inquiry.applicant_name],
    ["Email", inquiry.applicant_email],
    ["Status", inquiry.status],
    ["Zone", inquiry.current_zone],
    ["Score", inquiry.current_score],
    ["Recommendation", inquiry.recommendation],
    ["Operator action", inquiry.operator_action && `${inquiry.operator_action} (${inquiry.operator_action_by ?? "?"})`],
    ["Declined", inquiry.declined_at && `${fmtTime(inquiry.declined_at)} — ${inquiry.decline_reason ?? "no reason"}`],
    ["Not-verified reason", inquiry.not_verified_reason_label ?? inquiry.not_verified_reason],
    ["Follow-up reason", inquiry.follow_up_reason],
    ["Inquiry id", inquiry.inquiry_id],
    ["Person id", inquiry.person_id],
  ];
  return (
    <div className="rounded-md border border-slate-200 bg-white p-5">
      <dl className="grid grid-cols-1 gap-x-8 gap-y-1 sm:grid-cols-2 lg:grid-cols-3">
        {facts
          .filter(([, v]) => v)
          .map(([k, v]) => (
            <div key={k} className="flex gap-2 text-sm">
              <dt className="w-36 shrink-0 text-slate-500">{k}</dt>
              <dd className="text-slate-800 break-all">{v}</dd>
            </div>
          ))}
      </dl>
    </div>
  );
}

function VouchChain({ invitations }: { invitations: AdminInquiryTimeline["invitations"] }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-5">
      <h3 className="text-sm font-semibold text-slate-800 mb-3">Vouch chain</h3>
      <ul className="space-y-2">
        {invitations.map((v) => (
          <li key={v.invitation_id} className="text-sm text-slate-700">
            <span className="font-medium">{v.voucher_name}</span>
            <span className="text-slate-500"> · {v.voucher_email} · {v.relationship}</span>
            <span className="ml-2 rounded bg-slate-100 px-2 py-0.5 text-xs">{v.outcome ?? v.status}</span>
            {v.voucher_self_zone && (
              <span className="ml-2 text-xs text-slate-500">self-zone {v.voucher_self_zone}</span>
            )}
            {v.cancellation_reason && (
              <span className="ml-2 text-xs text-red-700">cancelled: {v.cancellation_reason}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function EventList({ events }: { events: TimelineEvent[] }) {
  if (events.length === 0) return <p className="text-sm text-slate-500">No events recorded.</p>;
  return (
    <ol className="space-y-1">
      {events.map((e, i) => {
        const p = describeEvent(e);
        const s = severityClasses(e.severity);
        return (
          <li key={`${e.at}-${e.event}-${i}`} className={`border-l-4 ${s.border} bg-white rounded-r-md px-4 py-2`}>
            <div className="flex items-baseline gap-3">
              <span className="w-40 shrink-0 font-mono text-xs text-slate-500">{fmtTime(e.at)}</span>
              <span className={`text-sm font-medium ${s.text}`}>{p.label}</span>
              {p.isVoucher && (
                <span className="rounded bg-violet-50 px-1.5 py-0.5 text-[11px] text-violet-700">voucher</span>
              )}
              {p.sub && <span className="text-sm text-slate-500">{p.sub}</span>}
            </div>
            <details className="ml-[10.75rem] mt-0.5">
              <summary className="cursor-pointer text-[11px] text-slate-400">raw</summary>
              <pre className="mt-1 overflow-x-auto rounded bg-slate-50 p-2 text-[11px] text-slate-600">
                {JSON.stringify(e.detail, null, 2)}
              </pre>
            </details>
          </li>
        );
      })}
    </ol>
  );
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toISOString().replace("T", " ").slice(0, 19) + "Z";
}
